import React, { Fragment, useState, useEffect, useContext } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import { withRouter } from 'react-router-dom';
import orderBy from 'lodash.orderby';
import filter from 'lodash.filter';
import classNames from 'classnames';
import scrollToComponent from 'react-scroll-to-component';

import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';

import EventListItem from '../components/content/lists/EventListItem';
import OrderCard from '../components/content/OrderCard';
import withNotificationDialogConsumer from '../components/hoc/withNotificationDialogConsumer';
import { FormattedMessageWrappedInSpan } from '../components/misc';

import { HelpdeskWidgetContext } from '../components/content/HelpdeskWidget'

import {
  NEW_SYSTEM_ORDER_REDIRECT_MSG,
  PAYMENT_COMPLETED,
  COMPLETED,
  PHOTO_DVD,
} from '../utils/variables';

const EVENTS_TAB = 0;
const ORDERS_TAB = 1;

const AccountPage = (props) => {

  var expandedOrderCardRef = null;

  const [expandedOrderCardId, setExpandedOrderCardId] = useState(null);
  const [scrollToOrderCard, setScrollToOrderCard] = useState(false);
  const [unpayedEvents, setUnpayedEvents] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(EVENTS_TAB);
  const [hlpDskState, hlpDskDispatch] = useContext(HelpdeskWidgetContext);


  const pendingOrders = filter(
    props.viewer.account.orders.edges,
    edge => edge.node.state !== PAYMENT_COMPLETED && edge.node.state !== COMPLETED
  );

  useEffect(() => {
    hlpDskDispatch({ type: 'name', payload: props.viewer.account.profile.name })
    hlpDskDispatch({ type: 'email', payload: props.viewer.account.profile.email })
  }, [])

  useEffect(() => {
    pendingOrders.forEach(pendingOrder => {
      pendingOrder.node.cart.lineItems.forEach(lineItem => {
        if (lineItem.product.type !== PHOTO_DVD) {
          setUnpayedEvents([...unpayedEvents,
          {
            ...lineItem.product.event,
            orderId: pendingOrder.node.orderid,
          }]);
        }
      });
    });

    const { match, location, history, onOpenNotificationDialog } = props;
    if (match.params.section === 'orders') {
      if (location.search === '?old') {
        onOpenNotificationDialog(NEW_SYSTEM_ORDER_REDIRECT_MSG, () => {
          history.replace(location.pathname);
        });
      }
    }
    const { location: { pathname: path } } = props;
    path.includes('/account/events') && setSelectedTabIndex(EVENTS_TAB);
    path.includes('/account/orders') && setSelectedTabIndex(ORDERS_TAB);
  }, [])

  useEffect(() => {
    setSelectedTabIndex(props.match.params.section === 'events' ? EVENTS_TAB : ORDERS_TAB)
  })

  useEffect(() => {
    if (scrollToOrderCard) {
      if (expandedOrderCardRef) {
        scrollToComponent(expandedOrderCardRef, { align: 'top' });
        setScrollToOrderCard(false);
      }
    }
  })

  //since we're navigating to the same key, only the URL is changing
  const handleTabChange = (event, value) => {
    switch (value) {
      case EVENTS_TAB:
        setSelectedTabIndex(EVENTS_TAB);
        props.history.replace('/account/events');
        break;
      case ORDERS_TAB:
        setSelectedTabIndex(ORDERS_TAB);
        props.history.replace('/account/orders');
        break;
      default:
        break;
    }
  }

  const handleNavigateHome = () => {
    props.history.push('/');
  };

  const handleUnpayedEventClick = (eventId, orderId) => {
    setExpandedOrderCardId(orderId);
    setScrollToOrderCard(true);
    handleTabChange(null, ORDERS_TAB);
  }

  const handleOrderCardExpandClick = (orderId) => {
    setExpandedOrderCardId(expandedOrderCardId === orderId ? null : orderId);
    setScrollToOrderCard(false);
  }

  const _renderInfoBox = () => {
    return (
      <div className="info-box-container mt-50 mb-50">
        <h2 className="center-text">
          <FormattedMessageWrappedInSpan
            id="accountPage.infoBox.oldOrdersAccess.title"
            defaultMessage="You are looking for your photos from older orders?"
          />
        </h2>
        <p>
          <FormattedMessageWrappedInSpan
            id="accountPage.infoBox.oldOrdersAccess.description"
            defaultMessage="No problem. Your photos haven't disappeared. So far we have not migrated all photos to our new system. But you can log in via {link} with your old order number and email address to get your other orders."
            values={{
              link: (
                <a
                  href="https://www.sportograf.com/account/signin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  www.sportograf.com
                </a>
              ),
            }}
          />
        </p>
      </div>
    );
  }

  const { intl, viewer } = props;
  const { events, orders } = viewer.account;
  const orderedEvents = orderBy(events.edges, ['node.beginsAt'], ['desc']);
  const orderedOrders = orderBy(orders.edges, ['node.paymentReceivedAt'], ['desc']);
  //show call to action if customer has no previous orders
  if (orderedEvents.length === 0 && orderedOrders.length === 0) {
    return (
      <Fragment>
        <div className="row">
          <div className="col-xs-16 col-sm-10 col-sm-offset-3">
            <div className="account-page-no-orders">
              <h1>
                <FormattedMessageWrappedInSpan
                  id="accountPage.emptyOrders.main"
                  defaultMessage="You have not placed any orders yet"
                />
              </h1>
              <div>
                <FormattedMessageWrappedInSpan
                  id="accountPage.emptyOrders.action"
                  defaultMessage="Feel free to browse the events and photos by clicking the button below"
                />
              </div>
              <button onClick={handleNavigateHome}>
                <FormattedMessageWrappedInSpan
                  id="accountPage.emptyOrders.actionButton"
                  defaultMessage="Browse events"
                />
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-16 col-sm-12 col-sm-offset-2">
            {_renderInfoBox()}
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <div className="account-page-container container-960 container-page">
      <div className="row">
        <div className="col-xs-16 col-sm-12 col-sm-offset-2">
          <Tabs
            value={selectedTabIndex}
            onChange={handleTabChange}
            variant="fullWidth"
          >
            <Tab
              classes={{ root: 'account-page-tab' }}
              label={intl.formatMessage(translations.tabOne)}
              value={EVENTS_TAB}
            />
            <Tab
              classes={{ root: 'account-page-tab' }}
              label={intl.formatMessage(translations.tabTwo)}
              value={ORDERS_TAB}
            />
          </Tabs>
          {selectedTabIndex === EVENTS_TAB && (
            <Fragment>
              {orderedEvents.length !== 0 && (
                <Fragment>
                  <h1>{intl.formatMessage(translations.tabOne)}</h1>
                  <div className="row">
                    {orderedEvents.map(edge => (
                      <EventListItem
                        event={edge.node}
                        hasFooter={false}
                        key={edge.node.id}
                        linkTo={`/account/event/${edge.node.sgId}`}
                      />
                    ))}
                  </div>
                </Fragment>
              )}
              {unpayedEvents.length !== 0 && (
                <Fragment>
                  <h1 className={classNames({ separator: orderedEvents.length !== 0 })}>
                    <FormattedMessageWrappedInSpan
                      id="accountPage.outstandingPayments.title"
                      defaultMessage="Outstanding Payments"
                    />
                  </h1>
                  <div className="row">
                    {unpayedEvents.map((event, index) => (
                      <EventListItem
                        bannerHasGrayScale={true}
                        event={event}
                        info={
                          <div>
                            <div className="event-list-item-date">
                              <p>
                                <FormattedMessageWrappedInSpan
                                  id="accountPage.eventListItem.orderId"
                                  defaultMessage="Order ID:"
                                />
                              </p>
                              <p>{event.orderId}</p>
                            </div>
                          </div>
                        }
                        hasFooter={false}
                        key={index}
                        onClick={eventId =>
                          handleUnpayedEventClick(eventId, event.orderId)
                        }
                      />
                    ))}
                  </div>
                </Fragment>
              )}
            </Fragment>
          )}
          {selectedTabIndex === ORDERS_TAB && (
            <div className="order-container">
              <h1>{intl.formatMessage(translations.tabTwo)}</h1>
              {orderedOrders.map(order => {
                const expanded = expandedOrderCardId === order.node.orderid;
                return (
                  <div
                    key={order.node.orderid}
                    ref={(elem) => {
                      if (expanded)
                        expandedOrderCardRef = elem
                    }
                    }>
                    <OrderCard
                      expanded={expanded}
                      key={order.node.id}
                      order={order.node}
                      onExpandClick={handleOrderCardExpandClick}

                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-xs-16 col-sm-12 col-sm-offset-2">
          {_renderInfoBox()}
        </div>
      </div>
    </div>
  );
}

const translations = defineMessages({
  tabOne: {
    id: 'accountPage.tabs.tabOne',
    defaultMessage: 'My photos',
  },
  tabTwo: {
    id: 'accountPage.tabs.tabTwo',
    defaultMessage: 'My invoices',
  },
});

AccountPage.propTypes = {
  viewer: PropTypes.object,
  intl: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  onOpenNotificationDialog: PropTypes.func,
};

export { AccountPage };

export default createFragmentContainer(
  injectIntl(withRouter(withNotificationDialogConsumer(AccountPage))),
  {
    viewer: graphql`
      fragment AccountPage_viewer on Viewer {
        account {
          profile {
            name
            email
          }
          events(first: $count) {
            edges {
              node {
                id
                sgId
                ...EventListItem_event
              }
            }
          }
          orders(first: $count) {
            edges {
              node {
                id
                cart {
                  lineItems {
                    product {
                      event {
                        id
                        sgId
                        ...EventListItem_event
                      }
                      type
                    }
                  }
                }
                orderid
                state
                ...OrderCard_order
              }
            }
          }
        }
      }
    `,
  }
);
